import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';

const PlanContext = createContext();

/**
 * Hook to access plan features and trial status.
 *
 * Returns { features, trial, loading, hasPlanFeature }
 *   - features:        raw feature map { feeManagement: true, aiMock: false, ... }
 *   - trial:           { active: true, expiresAt, daysLeft } or null
 *   - loading:         true while the first fetch is in-flight
 *   - hasPlanFeature:  (name) => boolean — returns true if the feature is enabled
 *                      (defaults to true if no plan / loading / error — fail open)
 *   - managedBy:       { name, contacts } when this institute is a distributor's
 *                      client — its plan is managed and paid for by the
 *                      distributor, so no screen may show it a price or a
 *                      purchase. null for everyone else.
 *   - distributor:     { status, readOnly } when this institute IS a distributor
 *                      (offer the Distributor dashboard). null otherwise.
 */
export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within PlanProvider');
  }
  return context;
};

/**
 * `hasPlanFeature` for a screen that only uses the plan to show or hide an
 * extra — it fails open (like `hasPlanFeature` itself) instead of throwing
 * when rendered outside PlanProvider, as in a component test.
 */
export const useHasPlanFeature = () => {
  const context = useContext(PlanContext);
  return context?.hasPlanFeature || (() => true);
};

/**
 * `hasStudentModule` for a screen that only uses the switch to show or hide an
 * extra — it answers false (closed) instead of throwing when rendered outside
 * PlanProvider, as in a component test.
 */
export const useHasStudentModule = () => {
  const context = useContext(PlanContext);
  return context?.hasStudentModule || (() => false);
};

export const PlanProvider = ({ children }) => {
  const [features, setFeatures] = useState({});
  const [planFeatures, setPlanFeatures] = useState({}); // plan-only values (ignoring institute module toggles)
  const [hiddenFeatures, setHiddenFeatures] = useState([]);
  const [trial, setTrial] = useState(null);
  const [grace, setGrace] = useState(null); // { inGrace, daysLeft, status }
  const [loading, setLoading] = useState(true);
  const [planLimitAlert, setPlanLimitAlert] = useState(null); // { message, limitField, current, max, noPlan }
  const [managedBy, setManagedBy] = useState(null);
  const [distributor, setDistributor] = useState(null);
  // The student-profile module switches (Settings → Modules): advancedProfile,
  // contractStudents. Default OFF and read `=== true` — unlike `features`,
  // which fails open on a key it does not know.
  const [studentModules, setStudentModules] = useState({});
  // Only the newest read may land: a session can change while one is in flight,
  // and a late answer for the last person must not overwrite the next one's.
  const latestRead = useRef(0);

  const fetchFeatures = useCallback(async () => {
    latestRead.current += 1;
    const read = latestRead.current;
    const superseded = () => read !== latestRead.current;
    try {
      const user = getCurrentUser();
      // Only fetch for institute-level users (admin/superadmin/instructor/student)
      // Tenantadmin has no plan restrictions
      if (!user || user.usertype === 'tenantadmin') {
        setFeatures({});
        setPlanFeatures({});
        setHiddenFeatures([]);
        setTrial(null);
        setGrace(null);
        setManagedBy(null);
        setDistributor(null);
        setStudentModules({});
        setLoading(false);
        return;
      }

      const { data } = await apiClient.get('/billing/plan-features');
      if (superseded()) return;
      setFeatures(data?.features || {});
      setPlanFeatures(data?.planFeatures || {});
      setHiddenFeatures(data?.hiddenFeatures || []);
      setTrial(data?.trial || null);
      setManagedBy(data?.managedBy || null);
      setDistributor(data?.distributor || null);
      setStudentModules(data?.studentModules || {});

      // Check for grace period from API response
      const trialGrace = data?.trial?.inGracePeriod
        ? { inGrace: true, daysLeft: data.trial.graceDaysRemaining, status: 'trial_expired' }
        : null;
      const subGrace = data?.subscriptionGrace?.inGracePeriod
        ? { inGrace: true, daysLeft: data.subscriptionGrace.graceDaysRemaining, status: 'subscription_expired' }
        : null;
      setGrace(trialGrace || subGrace || null);
    } catch (err) {
      if (superseded()) return;
      console.warn('Failed to fetch plan features, showing all modules:', err.message);
      setFeatures({}); // fail open
      setPlanFeatures({});
      setHiddenFeatures([]);
      setTrial(null);
      setGrace(null);
      setManagedBy(null);
      setDistributor(null);
      setStudentModules({});
    } finally {
      if (!superseded()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // ⚠️ This provider mounts before anyone has signed in, so its first read finds
  // no user. Without this, a login in the same tab kept that empty answer until
  // a reload: plan gates failed open, and anything that fails closed — a
  // student's My Profile — stayed hidden. Every session change re-reads. The
  // module switches drop at once, so one person's never show for the next.
  useEffect(() => {
    const onAuthChange = () => {
      setStudentModules({});
      setLoading(true);
      fetchFeatures();
    };
    window.addEventListener('vc-auth-change', onAuthChange);
    return () => window.removeEventListener('vc-auth-change', onAuthChange);
  }, [fetchFeatures]);

  // Listen for grace period events from apiClient interceptor
  useEffect(() => {
    const handleGrace = (e) => {
      setGrace(e.detail);
    };
    window.addEventListener('vc-plan-grace', handleGrace);
    return () => window.removeEventListener('vc-plan-grace', handleGrace);
  }, []);

  // Listen for plan-limit 403 events from apiClient interceptor
  useEffect(() => {
    const handleLimit = (e) => {
      setPlanLimitAlert(e.detail);
    };
    window.addEventListener('vc-plan-limit', handleLimit);
    return () => window.removeEventListener('vc-plan-limit', handleLimit);
  }, []);

  const dismissPlanLimitAlert = useCallback(() => setPlanLimitAlert(null), []);

  /**
   * Check if a feature is completely hidden (platform-level kill switch).
   * Hidden features should not appear at all — not even as locked.
   */
  const isFeatureHidden = useCallback(
    (featureName) => {
      if (!featureName) return false;
      return hiddenFeatures.includes(featureName);
    },
    [hiddenFeatures],
  );

  /**
   * Check if a specific plan feature is enabled.
   * Returns true (fail open) when:
   *   - Still loading
   *   - No features object (no plan assigned)
   *   - Feature key not present in the map
   */
  const hasPlanFeature = useCallback(
    (featureName) => {
      if (!featureName) return true;
      if (Object.keys(features).length === 0) return true; // no plan = show everything
      return features[featureName] !== false;
    },
    [features],
  );

  /**
   * Check if the plan itself allows a feature (ignoring institute module toggles).
   * Used by Settings → Modules to show "Not in plan" badge correctly.
   */
  const planAllowsFeature = useCallback(
    (featureName) => {
      if (!featureName) return true;
      if (Object.keys(planFeatures).length === 0) return true; // no plan data = assume allowed
      return planFeatures[featureName] !== false;
    },
    [planFeatures],
  );

  /**
   * Is a student-profile module switched on? Fails CLOSED: while loading, with
   * no answer, or for an unknown key it is off — a default-off module must
   * never flash on.
   */
  const hasStudentModule = useCallback(
    (key) => studentModules[key] === true,
    [studentModules],
  );

  return (
    <PlanContext.Provider value={{ features, planFeatures, hiddenFeatures, trial, grace, loading, hasPlanFeature, planAllowsFeature, isFeatureHidden, refreshPlan: fetchFeatures, planLimitAlert, dismissPlanLimitAlert, managedBy, distributor, studentModules, hasStudentModule }}>
      {children}
    </PlanContext.Provider>
  );
};
